import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, Phone, Mail, MapPin, Facebook, Twitter, Linkedin, Instagram, Image, Type, Sliders, Save, Eye, XCircle, Package, Folder, Users, TrendingUp, Star, HelpCircle, Upload, Building2, Popcorn, PlusCircle, Trash2, Link } from 'lucide-react'; // Importar Building2, Popcorn, PlusCircle, Trash2, Link
import * as LucideIcons from 'lucide-react'; // Importar todos los iconos de Lucide React
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../../context/SettingsContext';
import { products, categories } from '../../data/mockData'; // Importar datos para el slider

const SystemSettingsPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { settings, updateSettings } = useSettings();

  // Inicializar estados con valores por defecto si settings.popups o settings.sliderInfo son undefined
  const [companyName, setCompanyName] = useState(settings.companyName);
  const [companyLogoUrl, setCompanyLogoUrl] = useState(settings.companyLogoUrl);
  const [phone, setPhone] = useState(settings.phone);
  const [email, setEmail] = useState(settings.email);
  const [address, setAddress] = useState(settings.address);
  const [socialFacebook, setSocialFacebook] = useState(settings.social.facebook);
  const [socialTwitter, setSocialTwitter] = useState(settings.social.twitter);
  const [socialLinkedin, setSocialLinkedin] = useState(settings.social.linkedin);
  const [socialInstagram, setSocialInstagram] = useState(settings.social.instagram);
  const [sliderInfo, setSliderInfo] = useState(settings.sliderInfo || []); // Asegurar que sea un array vacío si es undefined
  const [popups, setPopups] = useState(settings.popups || []); // Asegurar que sea un array vacío si es undefined

  const [showPreview, setShowPreview] = useState(false);

  if (!isAdmin) {
    navigate('/admin');
    return null;
  }

  const handleSaveSettings = (e) => {
    e.preventDefault();
    updateSettings({
      companyName,
      companyLogoUrl,
      phone,
      email,
      address,
      social: {
        facebook: socialFacebook,
        twitter: socialTwitter,
        linkedin: socialLinkedin,
        instagram: socialInstagram,
      },
      sliderInfo,
      popups, // Guardar popups
    });
    alert('Configuración guardada exitosamente!');
    setShowPreview(false); // Ocultar previsualización después de guardar
  };

  const handleSliderInfoChange = (index, field, value) => {
    const newSliderInfo = [...sliderInfo];
    newSliderInfo[index] = { ...newSliderInfo[index], [field]: value };
    setSliderInfo(newSliderInfo);
  };

  // Funciones para Popups
  const handlePopupChange = (index, field, value) => {
    const newPopups = [...popups];
    newPopups[index] = { ...newPopups[index], [field]: value };
    setPopups(newPopups);
  };

  const handleAddPopup = () => {
    if (popups.length < 3) {
      setPopups([...popups, {
        id: Date.now(),
        title: 'Nuevo Popup',
        content: 'Contenido del nuevo popup.',
        imageUrl: '',
        buttonText: '',
        buttonLink: '',
        active: false,
        trigger: 'onLoad',
        showOnPages: ['/']
      }]);
    } else {
      alert('Solo puedes tener hasta 3 popups.');
    }
  };

  const handleDeletePopup = (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este popup?')) {
      setPopups(popups.filter(popup => popup.id !== id));
    }
  };

  const handlePopupImageUpload = (e, index) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        handlePopupChange(index, 'imageUrl', event.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      alert('Por favor, selecciona un archivo de imagen válido.');
    }
  };

  // Función para obtener el valor dinámico del slider
  const getDynamicSliderValue = (slideConfig) => {
    let value = slideConfig.value;
    const totalProducts = products.length;
    const totalCategories = categories.length;
    const totalUsers = 4; // Simulado, debería venir de un mock de usuarios o API
    const mostVisitedProduct = products.reduce((prev, current) => (prev.visits || 0) > (current.visits || 0) ? prev : current, { visits: 0 });
    const mostVisitedCategory = categories.reduce((prev, current) => (prev.visits || 0) > (current.visits || 0) ? prev : current, { visits: 0 });

    if (slideConfig.title === 'Productos Activos') {
      value = totalProducts;
    } else if (slideConfig.title === 'Categorías Listadas') {
      value = totalCategories;
    } else if (slideConfig.title === 'Usuarios Registrados') {
      value = totalUsers;
    } else if (slideConfig.title === 'Producto Estrella') {
      value = mostVisitedProduct.name || 'N/A';
      if (mostVisitedProduct.visits) {
        value += ` (${mostVisitedProduct.visits} vistas)`;
      }
    } else if (slideConfig.title === 'Categoría Top') {
      value = mostVisitedCategory.name || 'N/A';
      if (mostVisitedCategory.visits) {
        value += ` (${mostVisitedCategory.visits} vistas)`;
      }
    }
    return value;
  };

  // Drag and Drop para imágenes del slider
  const handleDragOver = (e) => {
    e.preventDefault(); // Permite el drop
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          handleSliderInfoChange(index, 'imageUrl', event.target.result);
          handleSliderInfoChange(index, 'icon', ''); // Limpiar icono si se sube imagen
        };
        reader.readAsDataURL(file);
      } else {
        alert('Por favor, suelta un archivo de imagen válido.');
      }
    }
  };

  const handleImageUpload = (e, index) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        handleSliderInfoChange(index, 'imageUrl', event.target.result);
        handleSliderInfoChange(index, 'icon', ''); // Limpiar icono si se sube imagen
      };
      reader.readAsDataURL(file);
    } else {
      alert('Por favor, selecciona un archivo de imagen válido.');
    }
  };

  // Componente de previsualización del Footer (movido aquí para acceso a estados locales)
  const PreviewFooter = ({ companyName, companyLogoUrl, phone, email, address, socialFacebook, socialTwitter, socialLinkedin, socialInstagram }) => (
    <motion.footer
      className="bg-gray-900 text-gray-300 py-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="flex flex-col items-center md:items-start text-center md:text-left">
          <div className="flex items-center gap-2 mb-4">
            {companyLogoUrl ? (
              <img src={companyLogoUrl} alt="Company Logo" className="w-10 h-10 rounded-xl object-cover" />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
            )}
            <span className="text-2xl font-bold text-white">{companyName}</span>
          </div>
          <p className="text-sm leading-relaxed">
            Tu socio estratégico para el éxito empresarial. Conectando empresas, simplificando procesos.
          </p>
        </div>
        <div className="text-center md:text-left">
          <h3 className="text-lg font-semibold text-white mb-4">Contáctanos</h3>
          <ul className="space-y-3">
            {phone && (<li className="flex items-center justify-center md:justify-start gap-3"><Phone className="w-5 h-5 text-blue-400" /><span>{phone}</span></li>)}
            {email && (<li className="flex items-center justify-center md:justify-start gap-3"><Mail className="w-5 h-5 text-blue-400" /><span>{email}</span></li>)}
            {address && (<li className="flex items-center justify-center md:justify-start gap-3"><MapPin className="w-5 h-5 text-blue-400" /><span>{address}</span></li>)}
          </ul>
        </div>
        <div className="text-center md:text-left">
          <h3 className="text-lg font-semibold text-white mb-4">Síguenos</h3>
          <div className="flex justify-center md:justify-start gap-4">
            {socialFacebook && (<a href={socialFacebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors"><Facebook className="w-7 h-7" /></a>)}
            {socialTwitter && (<a href={socialTwitter} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors"><Twitter className="w-7 h-7" /></a>)}
            {socialLinkedin && (<a href={socialLinkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors"><Linkedin className="w-7 h-7" /></a>)}
            {socialInstagram && (<a href={socialInstagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors"><Instagram className="w-7 h-7" /></a>)}
          </div>
        </div>
      </div>
      <div className="mt-10 pt-6 border-t border-gray-700 text-center text-sm text-gray-500">
        © {new Date().getFullYear()} {companyName}. Todos los derechos reservados.
      </div>
    </motion.footer>
  );

  // Componente de previsualización del Slider (movido aquí para acceso a estados locales)
  const PreviewSlider = ({ sliderInfo, getDynamicSliderValue }) => {
    const [previewIndex, setPreviewIndex] = useState(0);

    useEffect(() => {
      if (sliderInfo.length === 0) return; // Evitar errores si no hay slides
      const interval = setInterval(() => {
        setPreviewIndex((prevIndex) => (prevIndex + 1) % sliderInfo.length);
      }, 3000); // Cambia cada 3 segundos para la previsualización
      return () => clearInterval(interval);
    }, [sliderInfo.length]);

    const currentSlide = sliderInfo[previewIndex];
    const IconComponent = LucideIcons[currentSlide?.icon] || HelpCircle;

    return (
      <motion.div
        className="relative w-full max-w-md mx-auto lg:mx-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <AnimatePresence mode="wait">
          {currentSlide ? ( // Verificar si currentSlide existe
            <motion.div
              key={previewIndex}
              className="flex items-center gap-4 p-4 bg-white/70 backdrop-blur-md rounded-2xl shadow-md border border-white/20"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
            >
              {currentSlide.imageUrl ? (
                <img src={currentSlide.imageUrl} alt={currentSlide.title} className="w-12 h-12 rounded-xl object-cover" />
              ) : (
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${currentSlide.color}`}>
                  <IconComponent className="w-6 h-6 text-white" />
                </div>
              )}
              <div>
                <p className="text-sm font-semibold text-gray-600">{currentSlide.title}</p>
                <h4 className="text-lg font-bold text-gray-900">{getDynamicSliderValue(currentSlide)}</h4>
              </div>
            </motion.div>
          ) : (
            <div className="text-gray-500 text-center p-4">No hay slides configurados para previsualizar.</div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          className="mb-8 flex justify-between items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <Settings className="w-10 h-10 text-blue-600" />
              Configuración del Sistema
            </h2>
            <p className="text-xl text-gray-600">
              Gestiona la información general de la empresa, redes sociales y el contenido del slider.
            </p>
          </div>
          <motion.button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Eye className="w-5 h-5" />
            {showPreview ? 'Ocultar Previsualización' : 'Ver Previsualización'}
          </motion.button>
        </motion.div>

        <motion.form
          onSubmit={handleSaveSettings}
          className="bg-white/80 backdrop-blur-xl rounded-2xl p-8 shadow-lg border border-white/20 space-y-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Información de la Empresa */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Type className="w-6 h-6 text-blue-600" />
              Información General de la Empresa
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la Empresa</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL del Logo (pequeño)</label>
                <input
                  type="text"
                  value={companyLogoUrl}
                  onChange={(e) => setCompanyLogoUrl(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://ejemplo.com/logo.png"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Correo Electrónico</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                <textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                  rows="2"
                ></textarea>
              </div>
            </div>
          </div>

          {/* Redes Sociales */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Facebook className="w-6 h-6 text-blue-600" />
              Redes Sociales
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Facebook URL</label>
                <input
                  type="url"
                  value={socialFacebook}
                  onChange={(e) => setSocialFacebook(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://facebook.com/tuempresa"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Twitter URL</label>
                <input
                  type="url"
                  value={socialTwitter}
                  onChange={(e) => setSocialTwitter(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://twitter.com/tuempresa"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
                <input
                  type="url"
                  value={socialLinkedin}
                  onChange={(e) => setSocialLinkedin(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://linkedin.com/company/tuempresa"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Instagram URL</label>
                <input
                  type="url"
                  value={socialInstagram}
                  onChange={(e) => setSocialInstagram(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://instagram.com/tuempresa"
                />
              </div>
            </div>
          </div>

          {/* Configuración del Slider de Información */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Sliders className="w-6 h-6 text-blue-600" />
              Contenido del Slider de Información
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              Define el título, descripción y el icono para cada slide. Los valores numéricos se actualizarán automáticamente.
            </p>
            <div className="space-y-6">
              {sliderInfo.map((slide, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Slide {index + 1}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
                      <input
                        type="text"
                        value={slide.title}
                        onChange={(e) => handleSliderInfoChange(index, 'title', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Icono (Nombre de Lucide React)</label>
                      <input
                        type="text"
                        value={slide.icon}
                        onChange={(e) => handleSliderInfoChange(index, 'icon', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Ej: Package, Users, Star"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                      <textarea
                        value={slide.value}
                        onChange={(e) => handleSliderInfoChange(index, 'value', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                        rows="2"
                        required
                      ></textarea>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Color (TailwindCSS gradient)</label>
                      <input
                        type="text"
                        value={slide.color}
                        onChange={(e) => handleSliderInfoChange(index, 'color', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Ej: from-blue-500 to-blue-600"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">URL de Imagen (Opcional)</label>
                      <div
                        className="relative w-full h-32 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center cursor-pointer hover:border-blue-500 transition-colors"
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                      >
                        {slide.imageUrl ? (
                          <img src={slide.imageUrl} alt="Preview" className="max-h-full max-w-full object-contain" />
                        ) : (
                          <span className="text-gray-500">Arrastra y suelta una imagen aquí o haz clic para subir</span>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          onChange={(e) => handleImageUpload(e, index)}
                        />
                        {slide.imageUrl && (
                          <button
                            type="button"
                            onClick={() => handleSliderInfoChange(index, 'imageUrl', '')}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Configuración de Popups */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Popcorn className="w-6 h-6 text-blue-600" />
              Gestión de Popups
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              Configura hasta 3 popups para mostrar mensajes importantes a tus usuarios.
            </p>
            <motion.button
              type="button"
              onClick={handleAddPopup}
              disabled={popups.length >= 3}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-all duration-300 mb-6 disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <PlusCircle className="w-5 h-5" />
              Agregar Popup
            </motion.button>

            <div className="space-y-6">
              {popups.map((popup, index) => (
                <div key={popup.id} className="bg-gray-50 p-6 rounded-xl border border-gray-200 relative">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Popup {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => handleDeletePopup(popup.id)}
                    className="absolute top-4 right-4 p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
                      <input
                        type="text"
                        value={popup.title}
                        onChange={(e) => handlePopupChange(index, 'title', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Activo</label>
                      <select
                        value={popup.active}
                        onChange={(e) => handlePopupChange(index, 'active', e.target.value === 'true')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value={true}>Sí</option>
                        <option value={false}>No</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contenido</label>
                      <textarea
                        value={popup.content}
                        onChange={(e) => handlePopupChange(index, 'content', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                        rows="3"
                        required
                      ></textarea>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">URL de Imagen (Opcional)</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={popup.imageUrl}
                          onChange={(e) => handlePopupChange(index, 'imageUrl', e.target.value)}
                          className="flex-grow px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                          placeholder="https://ejemplo.com/imagen-popup.jpg"
                        />
                        <label className="px-4 py-3 bg-blue-100 text-blue-700 rounded-xl cursor-pointer hover:bg-blue-200 transition-colors">
                          <Upload className="w-5 h-5" />
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePopupImageUpload(e, index)} />
                        </label>
                      </div>
                      {popup.imageUrl && (
                        <div className="mt-2 relative w-24 h-24">
                          <img src={popup.imageUrl} alt="Popup Preview" className="w-full h-full object-cover rounded-lg" />
                          <button
                            type="button"
                            onClick={() => handlePopupChange(index, 'imageUrl', '')}
                            className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Texto del Botón (Opcional)</label>
                      <input
                        type="text"
                        value={popup.buttonText}
                        onChange={(e) => handlePopupChange(index, 'buttonText', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Enlace del Botón (Opcional)</label>
                      <input
                        type="url"
                        value={popup.buttonLink}
                        onChange={(e) => handlePopupChange(index, 'buttonLink', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Disparador</label>
                      <select
                        value={popup.trigger}
                        onChange={(e) => handlePopupChange(index, 'trigger', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="onLoad">Al cargar la página</option>
                        {/* <option value="onClick">Al hacer clic</option> */}
                        {/* <option value="onScroll">Al hacer scroll</option> */}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Mostrar en Páginas (separar por comas)</label>
                      <input
                        type="text"
                        value={popup.showOnPages.join(',')}
                        onChange={(e) => handlePopupChange(index, 'showOnPages', e.target.value.split(',').map(s => s.trim()))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                        placeholder="/admin, /"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <motion.button
            type="submit"
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-2xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Save className="w-5 h-5" />
            Guardar Configuración
          </motion.button>
        </motion.form>
      </div>

      <AnimatePresence>
        {showPreview && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center p-4 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-3xl p-8 shadow-2xl max-w-4xl w-full relative"
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
            >
              <button
                onClick={() => setShowPreview(false)}
                className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
              <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">Previsualización de Cambios</h3>

              <div className="mb-8">
                <h4 className="text-2xl font-bold text-gray-800 mb-4">Previsualización del Slider</h4>
                <div className="flex flex-col md:flex-row gap-4 justify-center">
                  {/* Slider con imagen */}
                  <PreviewSlider
                    sliderInfo={[{
                      title: "Ejemplo con Imagen",
                      value: "¡Mira qué bien se ve!",
                      icon: "", // No hay icono si hay imagen
                      color: "from-blue-500 to-blue-600",
                      imageUrl: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400" // Imagen de ejemplo
                    }]}
                    getDynamicSliderValue={(slide) => slide.value}
                  />
                  {/* Slider sin imagen (usando la configuración actual del primer slide) */}
                  {sliderInfo.length > 0 && (
                    <PreviewSlider
                      sliderInfo={[{
                        title: sliderInfo[0].title,
                        value: sliderInfo[0].value,
                        icon: sliderInfo[0].icon,
                        color: sliderInfo[0].color,
                        imageUrl: "" // Asegurarse de que no haya imagen para este ejemplo
                      }]}
                      getDynamicSliderValue={getDynamicSliderValue} // Pasar la función
                    />
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-2xl font-bold text-gray-800 mb-4">Previsualización del Footer</h4>
                <PreviewFooter
                  companyName={companyName}
                  companyLogoUrl={companyLogoUrl}
                  phone={phone}
                  email={email}
                  address={address}
                  socialFacebook={socialFacebook}
                  socialTwitter={socialTwitter}
                  socialLinkedin={socialLinkedin}
                  socialInstagram={socialInstagram}
                />
              </div>

              <div className="mt-8">
                <h4 className="text-2xl font-bold text-gray-800 mb-4">Previsualización de Popups</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {popups.length > 0 ? (
                    popups.map((popup, idx) => (
                      <div key={idx} className="bg-gray-100 p-4 rounded-xl border border-gray-200">
                        <h5 className="font-bold text-gray-900 mb-2">{popup.title}</h5>
                        {popup.imageUrl && <img src={popup.imageUrl} alt={popup.title} className="w-full h-24 object-cover rounded-lg mb-2" />}
                        <p className="text-sm text-gray-700 line-clamp-3">{popup.content}</p>
                        {popup.buttonText && <span className="mt-2 inline-block text-blue-600 text-sm font-medium">{popup.buttonText}</span>}
                        <div className="text-xs text-gray-500 mt-1">
                          Activo: {popup.active ? 'Sí' : 'No'} | Disparador: {popup.trigger}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 md:col-span-3 text-center">No hay popups configurados para previsualizar.</p>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SystemSettingsPage;

// Componente de previsualización del Footer (movido aquí para acceso a estados locales)
const PreviewFooter = ({ companyName, companyLogoUrl, phone, email, address, socialFacebook, socialTwitter, socialLinkedin, socialInstagram }) => (
  <motion.footer
    className="bg-gray-900 text-gray-300 py-10"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.3 }}
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
      <div className="flex flex-col items-center md:items-start text-center md:text-left">
        <div className="flex items-center gap-2 mb-4">
          {companyLogoUrl ? (
            <img src={companyLogoUrl} alt="Company Logo" className="w-10 h-10 rounded-xl object-cover" />
          ) : (
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
          )}
          <span className="text-2xl font-bold text-white">{companyName}</span>
        </div>
        <p className="text-sm leading-relaxed">
          Tu socio estratégico para el éxito empresarial. Conectando empresas, simplificando procesos.
        </p>
      </div>
      <div className="text-center md:text-left">
        <h3 className="text-lg font-semibold text-white mb-4">Contáctanos</h3>
        <ul className="space-y-3">
          {phone && (<li className="flex items-center justify-center md:justify-start gap-3"><Phone className="w-5 h-5 text-blue-400" /><span>{phone}</span></li>)}
          {email && (<li className="flex items-center justify-center md:justify-start gap-3"><Mail className="w-5 h-5 text-blue-400" /><span>{email}</span></li>)}
          {address && (<li className="flex items-center justify-center md:justify-start gap-3"><MapPin className="w-5 h-5 text-blue-400" /><span>{address}</span></li>)}
        </ul>
      </div>
      <div className="text-center md:text-left">
        <h3 className="text-lg font-semibold text-white mb-4">Síguenos</h3>
        <div className="flex justify-center md:justify-start gap-4">
          {socialFacebook && (<a href={socialFacebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors"><Facebook className="w-7 h-7" /></a>)}
          {socialTwitter && (<a href={socialTwitter} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors"><Twitter className="w-7 h-7" /></a>)}
          {socialLinkedin && (<a href={socialLinkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors"><Linkedin className="w-7 h-7" /></a>)}
          {socialInstagram && (<a href={socialInstagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors"><Instagram className="w-7 h-7" /></a>)}
        </div>
      </div>
    </div>
    <div className="mt-10 pt-6 border-t border-gray-700 text-center text-sm text-gray-500">
      © {new Date().getFullYear()} {companyName}. Todos los derechos reservados.
    </div>
  </motion.footer>
);

// Componente de previsualización del Slider (movido aquí para acceso a estados locales)
const PreviewSlider = ({ sliderInfo, getDynamicSliderValue }) => {
  const [previewIndex, setPreviewIndex] = useState(0);

  useEffect(() => {
    if (sliderInfo.length === 0) return; // Evitar errores si no hay slides
    const interval = setInterval(() => {
      setPreviewIndex((prevIndex) => (prevIndex + 1) % sliderInfo.length);
    }, 3000); // Cambia cada 3 segundos para la previsualización
    return () => clearInterval(interval);
  }, [sliderInfo.length]);

  const currentSlide = sliderInfo[previewIndex];
  const IconComponent = LucideIcons[currentSlide?.icon] || HelpCircle;

  return (
    <motion.div
      className="relative w-full max-w-md mx-auto lg:mx-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <AnimatePresence mode="wait">
        {currentSlide ? ( // Verificar si currentSlide existe
          <motion.div
            key={previewIndex}
            className="flex items-center gap-4 p-4 bg-white/70 backdrop-blur-md rounded-2xl shadow-md border border-white/20"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5 }}
          >
            {currentSlide.imageUrl ? (
              <img src={currentSlide.imageUrl} alt={currentSlide.title} className="w-12 h-12 rounded-xl object-cover" />
            ) : (
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${currentSlide.color}`}>
                <IconComponent className="w-6 h-6 text-white" />
              </div>
            )}
            <div>
              <p className="text-sm font-semibold text-gray-600">{currentSlide.title}</p>
              <h4 className="text-lg font-bold text-gray-900">{getDynamicSliderValue(currentSlide)}</h4>
            </div>
          </motion.div>
        ) : (
          <div className="text-gray-500 text-center p-4">No hay slides configurados para previsualizar.</div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};